/**
 * @file HeaderLookFields.tsx
 * @description Подгруппа «Вид шапки» шапки раздела (phase19.16, партия
 * SB120, этап 1; uiux-sb120-stage1 §2.7; данные db-design-sb120-stage1 §1
 * `header.color`, `header.line`): цвет шапки (`ColorField mode="text"`,
 * «Авто» — нет ключа, по фону) и линия до ссылки — толщина 0–4 px (0 —
 * без линии) и её цвет («↺ как у сайта» — нет ключа, `--site-line`).
 * Линия не рисуется на телефоне и при заголовке по центру (как и раньше).
 * Шрифт шапки — этап 3 (нужен список своих шрифтов магазина от API).
 */

import { DEFAULT_SITE_THEME, SITE_TEXT_COLOR_ROLES, type SectionBg, type SectionHeader, type SiteTextColor } from "@chaiberi/shared";

import { SettingsSubgroup } from "../../../ui/settings-groups";
import { ColorField, type ColorTextValue } from "../../../site-fields/ColorField";
import { NumberSliderField } from "../../../site-fields/NumberSliderField";
import { useSectionForm } from "../section-form-context";
import { ownValue } from "../../../../lib/site-fields/number-input";
import { sectionBgSample } from "../../../../lib/site-builder/text-style";
import { TEXT_COLOR_ROLE_LABELS as ROLE_LABELS } from "../../../../lib/site-fields/text-color-labels";

type Role = (typeof SITE_TEXT_COLOR_ROLES)[number];

/** «Авто» поля ↔ нет ключа. */
const toField = (color: SiteTextColor | undefined): ColorTextValue<Role> => color ?? { kind: "auto" };
const fromField = (value: ColorTextValue<Role>): SiteTextColor | undefined => (value.kind === "auto" ? undefined : value);

interface Props {
  header: SectionHeader;
  /** Фон секции — для контраста цвета шапки. */
  bg: SectionBg;
  onChange: (header: SectionHeader) => void;
}

export function HeaderLookFields({ header, bg, onChange }: Props) {
  const { preview, theme } = useSectionForm();
  const colors = (theme ?? DEFAULT_SITE_THEME).colors;
  const sample = sectionBgSample(bg, colors);
  const setColor = (color: SiteTextColor | undefined) => {
    const next = { ...header };
    if (color) next.color = color;
    else delete next.color;
    onChange(next);
  };
  const setLine = (line: SectionHeader["line"]) => {
    const next = { ...line };
    if (!next.color) delete next.color;
    onChange({ ...header, line: next });
  };

  return (
    <SettingsSubgroup title="Вид шапки">
      <ColorField mode="text" label="Цвет шапки" value={toField(header.color)} roles={SITE_TEXT_COLOR_ROLES} labels={ROLE_LABELS} colors={colors} elementBg={sample} onChange={(v) => setColor(fromField(v))} />
      <NumberSliderField
        label="Линия до ссылки"
        integer
        value={ownValue(header.line.width, 0)}
        inherit={{ value: 0, label: "без линии" }}
        min={0}
        max={4}
        step={1}
        unit="px"
        presets={[
          { label: "Тонкая 1", value: 1 },
          { label: "2", value: 2 },
        ]}
        hint="На телефоне и при заголовке по центру линии нет."
        onChange={(width) => setLine({ ...header.line, width: width ?? 0 })}
        onPreview={(v) => preview("header.line.width", v ?? 0)}
      />
      {header.line.width > 0 ? (
        <ColorField
          mode="text"
          label="Цвет линии"
          autoLabel="Как у сайта"
          value={toField(header.line.color)}
          roles={SITE_TEXT_COLOR_ROLES}
          labels={ROLE_LABELS}
          colors={colors}
          elementBg={sample}
          onChange={(v) => setLine({ ...header.line, color: fromField(v) })}
        />
      ) : null}
    </SettingsSubgroup>
  );
}
